/**
 * Planning Hub Page (Premium)
 * 
 * Unified view for premium meal planning:
 * - Next 7 days scheduled meals at a glance
 * - Quick actions: generate grocery list, create menu plan, create occasion
 * - Active grocery list display
 * - Upcoming menu plans
 * 
 * Uses playful premium styling:
 * - Beige base background
 * - Hairline borders
 * - Pixel art vibe
 * - Subtle produce-themed background imagery
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { PageHeading } from '../components/ui';
import { getSubscriptionStatus } from '../services/subscriptionService';
import {
    getPlanningHubData,
    generateGroceryListFromSchedule,
    createMenuPlanFromSchedule,
    createOccasionFromSchedule,
    getOccasionRelatedData
} from '../services/unifiedPlanningService';
import { getOccasions, createOccasion, deleteOccasion } from '../services/api';
import PremiumPlanningShell from '../components/PremiumPlanningShell';
import OccasionDetailModal from '../components/planning/OccasionDetailModal';
import '../styles/planningHub.css';

// Color tokens matching design system
const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral50: '#F9F8F6'
};

const PlanningHubPage = () => {
    const { user, userProfile } = useAuth();
    const navigate = useNavigate();
    const [hasAccess, setHasAccess] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hubData, setHubData] = useState(null);
    const [occasions, setOccasions] = useState([]);
    const [actionLoading, setActionLoading] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedOccasionId, setSelectedOccasionId] = useState(null);
    const [deleteConfirmData, setDeleteConfirmData] = useState(null);

    // Get planning theme from user profile or localStorage
    const planningTheme = userProfile?.session_preferences?.planning_theme || 
                        localStorage.getItem('planning_theme') || 
                        'tomato';
    
    const backgroundImages = {
        tomato: '/images/menuScheduling/tomato_bg.png',
        lemon: '/images/menuScheduling/lemon_bg.png',
        spinach: '/images/menuScheduling/spinach_bg.png'
    };

    // Get theme for a card based on index (cycles through themes)
    const getCardTheme = (index) => {
        const themes = ['tomato', 'lemon', 'spinach'];
        return themes[index % themes.length];
    };

    // Check premium access
    useEffect(() => {
        const checkAccess = async () => {
            try {
                const status = await getSubscriptionStatus();
                const isPremium = ['unlimited', 'admin', 'beta', 'family'].includes(status.planType);
                setHasAccess(isPremium);
                
                if (!isPremium) {
                    navigate('/profile?tab=subscription');
                }
            } catch (err) {
                console.error('Failed to check subscription:', err);
                setHasAccess(false);
                navigate('/profile?tab=subscription');
            }
        };
        checkAccess();
    }, [navigate]);

    // Load hub data
    useEffect(() => {
        if (hasAccess && user) {
            loadHubData();
        }
    }, [hasAccess, user]);

    // Listen for grocery list, menu plan, and occasion updates from other pages
    useEffect(() => {
        const handleGroceryListUpdate = () => {
            console.log('[PlanningHub] Grocery list updated, refreshing data...');
            loadHubData();
        };

        const handleMenuPlanUpdate = () => {
            console.log('[PlanningHub] Menu plan created/updated, refreshing data...');
            loadHubData();
        };

        const handleOccasionUpdate = () => {
            console.log('[PlanningHub] Occasion created/updated, refreshing data...');
            loadHubData();
        };

        // Listen for custom events when lists/plans/occasions are created/updated
        window.addEventListener('groceryListCreated', handleGroceryListUpdate);
        window.addEventListener('groceryListUpdated', handleGroceryListUpdate);
        window.addEventListener('menuPlanCreated', handleMenuPlanUpdate);
        window.addEventListener('menuPlanUpdated', handleMenuPlanUpdate);
        window.addEventListener('occasionCreated', handleOccasionUpdate);
        window.addEventListener('occasionDeleted', handleOccasionUpdate);
        
        // Also listen for visibility changes to refresh when user returns to tab
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && hasAccess && user) {
                loadHubData();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('groceryListCreated', handleGroceryListUpdate);
            window.removeEventListener('groceryListUpdated', handleGroceryListUpdate);
            window.removeEventListener('menuPlanCreated', handleMenuPlanUpdate);
            window.removeEventListener('menuPlanUpdated', handleMenuPlanUpdate);
            window.removeEventListener('occasionCreated', handleOccasionUpdate);
            window.removeEventListener('occasionDeleted', handleOccasionUpdate);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [hasAccess, user]);

    const loadHubData = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await user.getIdToken();
            const data = await getPlanningHubData(token);
            setHubData(data);
            
            // Log for debugging
            console.log('[PlanningHub] Hub data:', {
                scheduledMeals: data.scheduledMeals?.length || 0,
                activeGroceryList: data.activeGroceryList?.id || null,
                allGroceryLists: data.allGroceryLists?.length || 0,
                menuPlans: data.menuPlans?.length || 0
            });
            
            // Log grocery lists with occasion info for debugging
            if (data.allGroceryLists && data.allGroceryLists.length > 0) {
                console.log('[PlanningHub] Grocery lists with occasion info:', data.allGroceryLists.map(list => ({
                    id: list.id,
                    name: list.name,
                    occasion_id: list.occasion_id,
                    occasion_name: list.occasion_name,
                    itemCount: list.itemCount || (Array.isArray(list.items) ? list.items.length : 0)
                })));
            }
            
            // Load occasions
            const occasionsResponse = await getOccasions(token);
            const occasionsList = occasionsResponse.occasions || occasionsResponse || [];
            console.log('[PlanningHub] All occasions:', occasionsList.map(occ => ({
                id: occ.id,
                name: occ.name,
                type: occ.occasion_type
            })));
            
            // Filter for occasions that are linked to scheduled meals (have occasion_id on scheduled_meals)
            // or are of type 'scheduled_range' or 'menu_plan'
            // Also deduplicate by ID to prevent showing the same occasion twice
            const seenIds = new Set();
            const scheduledOccasions = occasionsList
                .filter(occ => {
                    // Filter by type
                    const matchesType = occ.occasion_type === 'scheduled_range' || 
                                      occ.occasion_type === 'menu_plan' ||
                                      occ.occasion_type === 'custom';
                    // Deduplicate by ID
                    if (matchesType && occ.id && !seenIds.has(occ.id)) {
                        seenIds.add(occ.id);
                        return true;
                    }
                    return false;
                });
            console.log('[PlanningHub] Filtered scheduled occasions:', scheduledOccasions.map(occ => ({
                id: occ.id,
                name: occ.name,
                type: occ.occasion_type
            })));
            setOccasions(scheduledOccasions);
        } catch (err) {
            console.error('Error loading hub data:', err);
            setError(err.message || 'Failed to load planning data');
        } finally {
            setLoading(false);
        }
    };

    // Format date for display
    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        if (date.toDateString() === today.toDateString()) return 'Today';
        if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
        
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // Get date strings for API calls
    const getDateStrings = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        const formatISO = (d) => d.toISOString().split('T')[0];
        return {
            startDate: formatISO(today),
            endDate: formatISO(nextWeek)
        };
    };

    // Handle quick actions
    const handleGenerateGroceryList = async () => {
        try {
            setActionLoading('grocery');
            const token = await user.getIdToken();
            const { startDate, endDate } = getDateStrings();
            
            await generateGroceryListFromSchedule(startDate, endDate, {}, token);
            setSuccessMessage('Grocery list generated successfully!');
            
            // Dispatch event to notify other pages (like grocery list page)
            window.dispatchEvent(new CustomEvent('groceryListCreated', {
                detail: { dateRange: { start: startDate, end: endDate } }
            }));
            
            loadHubData(); // Refresh data
            
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const handleCreateMenuPlan = () => {
        // Navigate to menu planning page
        navigate('/menu-planning');
    };

    const handleCreateOccasion = async () => {
        const name = prompt('Name your occasion (e.g., "This Week\'s Meals"):');
        if (!name) return;

        try {
            setActionLoading('occasion');
            const token = await user.getIdToken();
            const { startDate, endDate } = getDateStrings();
            
            await createOccasionFromSchedule(startDate, endDate, name, token);
            setSuccessMessage('Occasion created successfully!');
            loadHubData();
            
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setActionLoading(null);
        }
    };

    // Group meals by date
    const getMealsByDate = () => {
        if (!hubData?.scheduledMeals) return {};
        
        const grouped = {};
        for (const meal of hubData.scheduledMeals) {
            const dateKey = new Date(meal.scheduled_date).toISOString().split('T')[0];
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(meal);
        }
        return grouped;
    };

    // Loading state
    if (hasAccess === null || loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: colorTokens.alabaster }}>
                <div className="flex items-center justify-center h-64">
                    <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                        Loading planning hub...
                    </p>
                </div>
            </div>
        );
    }

    // No access
    if (!hasAccess) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: colorTokens.alabaster }}>
                <div className="max-w-lg mx-auto py-16 px-4">
                    <div className="bg-white p-8 hairline text-center">
                        <h1 className="font-sans text-3xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                            Premium Feature
                        </h1>
                        <p className="font-sans text-base text-neutral-900 opacity-80 mb-6">
                            The Planning Hub is available for premium members.
                        </p>
                        <button
                            onClick={() => navigate('/subscription')}
                            className="px-4 py-2 font-mono text-sm uppercase tracking-wider"
                            style={{ backgroundColor: colorTokens.oxfordBlue, color: '#FFFFFF' }}
                        >
                            Upgrade Now
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const mealsByDate = getMealsByDate();
    const mealDates = Object.keys(mealsByDate).sort();

    return (
        <div className="min-h-screen bg-neutral-50">
            <div className="w-full px-0 py-2 md:py-4">
                {/* Page Header */}
                <div className="page-heading-container hairline min-h-[32vh] md:min-h-[48vh] relative mb-8">
                    <div className="page-heading-content absolute bottom-0 left-2 right-2 md:left-4 md:right-4">
                        <div className="flex items-center gap-3">
                            <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
                                PLANNING HUB
                            </h1>
                            <span 
                                className="px-2 py-1 font-mono text-xs uppercase tracking-wider"
                                style={{ backgroundColor: colorTokens.oxfordBlue, color: '#FFFFFF' }}
                            >
                                PREMIUM
                            </span>
                        </div>
                        <p className="page-heading-subtitle font-mono uppercase tracking-wider text-neutral-900 opacity-60 mt-0 text-sm md:text-lg">
                            Your unified meal planning command center
                        </p>
                    </div>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="mb-4">
                        <div className="bg-neutral-0 hairline minimal-padding">
                            <div className="p-4 bg-red-50 hairline text-red-900 font-mono text-sm">
                                {error}
                                <button onClick={() => setError(null)} className="float-right font-mono">×</button>
                            </div>
                        </div>
                    </div>
                )}
                {successMessage && (
                    <div className="mb-4">
                        <div className="bg-neutral-0 hairline minimal-padding">
                            <div className="p-4 bg-green-50 hairline text-green-900 font-mono text-sm">
                                {successMessage}
                            </div>
                        </div>
                    </div>
                )}

                <div className="bg-neutral-0 hairline minimal-padding vspace">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        {/* Left Column: Upcoming Meals */}
                        <div className="lg:col-span-2">
                            <div 
                                className="bg-white hairline p-6 relative overflow-hidden"
                                style={{
                                    backgroundImage: `url(${backgroundImages[planningTheme]})`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                {/* Content overlay */}
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-6">
                                        <h2 
                                            className="font-sans text-2xl font-medium"
                                            style={{ color: '#FFFFFF' }}
                                        >
                                            Next 7 Days
                                        </h2>
                                        <button
                                            onClick={() => navigate('/menu-scheduling')}
                                            className="px-3 py-1.5 font-mono text-xs uppercase tracking-wider hairline bg-neutral-50 hover:bg-neutral-900 hover:text-white transition-all"
                                        >
                                            View Calendar →
                                        </button>
                                    </div>

                                    {mealDates.length === 0 ? (
                                        <div className="text-center py-12 bg-white/90 hairline">
                                            <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 mb-4">
                                                No meals scheduled for the next 7 days
                                            </p>
                                            <button
                                                onClick={() => navigate('/menu-scheduling')}
                                                className="px-4 py-2 font-mono text-sm uppercase tracking-wider"
                                                style={{ backgroundColor: colorTokens.oxfordBlue, color: '#FFFFFF' }}
                                            >
                                                Schedule Meals
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {mealDates.map(dateKey => (
                                                <div key={dateKey} className="bg-white/95 hairline p-4">
                                                    <h3 className="font-mono text-sm uppercase tracking-wider mb-3" style={{ color: colorTokens.oxfordBlue }}>
                                                        {formatDate(dateKey)}
                                                    </h3>
                                                    <div className="space-y-2">
                                                        {mealsByDate[dateKey].map(meal => (
                                                            <div 
                                                                key={meal.id} 
                                                                className="flex items-center gap-3 p-2 bg-neutral-50 hairline"
                                                            >
                                                                {meal.meal_type && (
                                                                    <span className="font-mono text-xs uppercase tracking-wider px-2 py-0.5 bg-neutral-200">
                                                                        {meal.meal_type}
                                                                    </span>
                                                                )}
                                                                <span className="font-sans text-base flex-1">
                                                                    {meal.recipe_name}
                                                                </span>
                                                                {meal.requires_day_before_prep && (
                                                                    <span className="font-mono text-xs text-yellow-700" title="Requires day-before prep">
                                                                        ⚠
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Scheduled Occasions */}
                            <div 
                                className="mt-6 hairline p-6 relative overflow-hidden"
                                style={{
                                    backgroundImage: `url(${backgroundImages[getCardTheme(1)]})`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 
                                            className="font-sans text-xl font-medium"
                                            style={{ color: '#FFFFFF' }}
                                        >
                                            Scheduled Occasions
                                        </h2>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={async () => {
                                                    const name = prompt('Name your occasion:');
                                                    if (!name) return;
                                                    try {
                                                        const token = await user.getIdToken();
                                                        await createOccasion({ name, occasion_type: 'custom' }, token);
                                                        loadHubData();
                                                    } catch (err) {
                                                        setError(err.message || 'Failed to create occasion');
                                                    }
                                                }}
                                                className="px-2 py-1 font-mono text-xs uppercase tracking-wider bg-white text-neutral-900 hover:bg-neutral-900 hover:text-white transition-all"
                                                title="Add New Occasion"
                                            >
                                                +
                                            </button>
                                            <button
                                                onClick={() => navigate('/menu-planning')}
                                                className="px-3 py-1.5 font-mono text-xs uppercase tracking-wider bg-white text-neutral-900 hover:bg-neutral-900 hover:text-white transition-all"
                                            >
                                                View All →
                                            </button>
                                        </div>
                                    </div>

                                    {occasions.length === 0 ? (
                                        <p className="font-mono text-sm text-neutral-900 opacity-60 text-center py-6 bg-white hairline p-4">
                                            No occasions scheduled
                                        </p>
                                    ) : (
                                        <div className="space-y-3">
                                            {occasions.slice(0, 5).map((occasion, index) => {
                                                return (
                                                    <div
                                                        key={occasion.id}
                                                        className="bg-white hairline p-4 cursor-pointer hover:bg-neutral-900 hover:text-white transition-all relative group"
                                                        onClick={(e) => {
                                                            // Don't open modal if clicking delete button
                                                            if (!e.target.closest('.delete-occasion-btn')) {
                                                                setSelectedOccasionId(occasion.id);
                                                            }
                                                        }}
                                                    >
                                                        {/* Delete Button */}
                                                        <button
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    const token = await user.getIdToken();
                                                                    // Get related data to show in confirmation
                                                                    const relatedData = await getOccasionRelatedData(occasion.id, token);
                                                                    setDeleteConfirmData({
                                                                        occasion,
                                                                        groceryListsCount: relatedData.groceryLists?.length || 0,
                                                                        menuPlansCount: relatedData.menuPlans?.length || 0
                                                                    });
                                                                } catch (err) {
                                                                    console.error('Error loading occasion data:', err);
                                                                    // Still show confirmation even if we can't load related data
                                                                    setDeleteConfirmData({
                                                                        occasion,
                                                                        groceryListsCount: 0,
                                                                        menuPlansCount: 0
                                                                    });
                                                                }
                                                            }}
                                                            className="delete-occasion-btn absolute top-2 right-2 z-10 w-6 h-6 flex items-center justify-center bg-red-500 text-white hover:bg-red-600 transition-colors font-mono text-sm border-none opacity-0 group-hover:opacity-100"
                                                            aria-label="Delete occasion"
                                                            title="Delete occasion"
                                                        >
                                                            ×
                                                        </button>
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <h3 className="font-sans text-base font-medium mb-1" style={{ color: colorTokens.oxfordBlue }}>
                                                                    {occasion.name}
                                                                </h3>
                                                                {occasion.description && (
                                                                    <p className="font-sans text-xs text-neutral-900 opacity-60 mb-2">
                                                                        {occasion.description}
                                                                    </p>
                                                                )}
                                                                <div className="flex items-center gap-3">
                                                                    <span className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                                                        {occasion.recipe_count || 0} {occasion.recipe_count === 1 ? 'recipe' : 'recipes'}
                                                                    </span>
                                                                    {occasion.occasion_type && (
                                                                        <span className="font-mono text-xs uppercase tracking-wider px-2 py-0.5 bg-neutral-200 text-neutral-900">
                                                                            {occasion.occasion_type}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {occasions.length > 5 && (
                                                <p className="font-mono text-xs text-neutral-900 opacity-60 text-center pt-2">
                                                    + {occasions.length - 5} more occasions
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Active List & Plans */}
                        <div className="space-y-6">
                            {/* Active Grocery List */}
                            <div 
                                className="hairline p-6 relative overflow-hidden"
                                style={{
                                    backgroundImage: `url(${backgroundImages.spinach})`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                <div className="relative z-10">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 
                                        className="font-sans text-xl font-medium"
                                        style={{ color: '#FFFFFF' }}
                                    >
                                        Grocery List
                                    </h2>
                                    <button
                                        onClick={() => navigate('/grocery-list')}
                                        className="px-3 py-1.5 font-mono text-xs uppercase tracking-wider bg-white text-neutral-900 hover:bg-neutral-900 hover:text-white transition-all"
                                    >
                                        View All →
                                    </button>
                                </div>
                                
                                {(() => {
                                    // Show all grocery lists, prioritizing active list
                                    const allLists = hubData?.allGroceryLists || [];
                                    const activeList = hubData?.activeGroceryList;
                                    
                                    // Combine active list with all lists, removing duplicates
                                    const listsToShow = [];
                                    if (activeList && activeList.id) {
                                        listsToShow.push(activeList);
                                    }
                                    allLists.forEach(list => {
                                        if (!listsToShow.find(l => l.id === list.id)) {
                                            listsToShow.push(list);
                                        }
                                    });
                                    
                                    if (listsToShow.length > 0) {
                                        return (
                                            <div className="space-y-2">
                                                {listsToShow.slice(0, 3).map(list => {
                                                    const itemCount = Array.isArray(list.items) ? list.items.length : (list.itemCount || 0);
                                                    const checkedCount = Array.isArray(list.items) ? list.items.filter(i => i.checked).length : 0;
                                                    const remainingCount = itemCount - checkedCount;
                                                    
                                                    return (
                                                        <div 
                                                            key={list.id}
                                                            className="p-3 bg-white hairline cursor-pointer hover:bg-neutral-900 hover:text-white transition-all"
                                                            onClick={() => navigate(`/grocery-list?listId=${list.id}`)}
                                                        >
                                                            <p className="font-sans text-sm font-medium truncate">
                                                                {list.name}
                                                            </p>
                                                            <p className="font-mono text-xs opacity-60 mt-1">
                                                                {remainingCount} items remaining
                                                                {list.occasion_name && ` • ${list.occasion_name}`}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                                {listsToShow.length > 3 && (
                                                    <p className="font-mono text-xs text-neutral-900 opacity-60 text-center pt-2">
                                                        + {listsToShow.length - 3} more lists
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <p className="font-mono text-sm text-neutral-900 opacity-60 text-center py-6 bg-white hairline p-4">
                                                No grocery lists
                                            </p>
                                        );
                                    }
                                })()}
                                </div>
                            </div>

                            {/* Menu Plans */}
                            <div 
                                className="hairline p-6 relative overflow-hidden"
                                style={{
                                    backgroundImage: `url(${backgroundImages.tomato})`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}
                            >
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 
                                            className="font-sans text-xl font-medium"
                                            style={{ color: '#FFFFFF' }}
                                        >
                                            Menu Plans
                                        </h2>
                                        <button
                                            onClick={() => navigate('/menu-planning')}
                                            className="px-3 py-1.5 font-mono text-xs uppercase tracking-wider bg-white text-neutral-900 hover:bg-neutral-900 hover:text-white transition-all"
                                        >
                                            View All →
                                        </button>
                                    </div>

                                    {Array.isArray(hubData?.menuPlans) && hubData.menuPlans.length > 0 ? (
                                        <div className="space-y-2">
                                            {hubData.menuPlans.slice(0, 3).map(plan => {
                                                const recipeCount = Array.isArray(plan.recipe_ids) ? plan.recipe_ids.length : (Array.isArray(plan.recipe_names) ? plan.recipe_names.length : 0);
                                                return (
                                                    <div 
                                                        key={plan.id}
                                                        className="p-3 bg-white hairline cursor-pointer hover:bg-neutral-900 hover:text-white transition-all"
                                                        onClick={() => navigate(`/menu-planning?planId=${plan.id}`)}
                                                    >
                                                        <p className="font-sans text-sm font-medium truncate">
                                                            {plan.name}
                                                        </p>
                                                        <p className="font-mono text-xs opacity-60 mt-1">
                                                            {recipeCount} {recipeCount === 1 ? 'recipe' : 'recipes'}
                                                        </p>
                                                    </div>
                                                );
                                            })}
                                            {hubData.menuPlans.length > 3 && (
                                                <p className="font-mono text-xs text-neutral-900 opacity-60 text-center pt-2">
                                                    + {hubData.menuPlans.length - 3} more plans
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="font-mono text-sm text-neutral-900 opacity-60 text-center py-6 bg-white hairline p-4">
                                            No menu plans
                                        </p>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Premium Features - Full Width */}
                    <div 
                        className="mt-6 hairline p-6 relative overflow-hidden"
                        style={{
                            backgroundImage: `url(${backgroundImages[getCardTheme(2)]})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                        }}
                    >
                        <div className="relative z-10">
                            <h2 
                                className="font-sans text-xl font-medium mb-4"
                                style={{ color: '#FFFFFF' }}
                            >
                                Premium Features
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <button
                                    onClick={() => navigate('/menu-scheduling')}
                                    className="p-3 text-left bg-white hairline hover:bg-neutral-900 hover:text-white transition-all group"
                                >
                                    <span className="font-mono text-sm uppercase tracking-wider flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                        Menu Scheduling →
                                    </span>
                                </button>
                                <button
                                    onClick={() => navigate('/menu-planning')}
                                    className="p-3 text-left bg-white hairline hover:bg-neutral-900 hover:text-white transition-all group"
                                >
                                    <span className="font-mono text-sm uppercase tracking-wider flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                        Menu Planning →
                                    </span>
                                </button>
                                <button
                                    onClick={() => navigate('/grocery-list')}
                                    className="p-3 text-left bg-white hairline hover:bg-neutral-900 hover:text-white transition-all group"
                                >
                                    <span className="font-mono text-sm uppercase tracking-wider flex items-center gap-2">
                                        <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                        Grocery Lists →
                                    </span>
                                </button>
                                <div className="relative group">
                                    <button
                                        onClick={() => {
                                            if (userProfile?.is_admin) {
                                                navigate('/kitchen-stock');
                                            }
                                        }}
                                        disabled={!userProfile?.is_admin}
                                        className={`p-3 text-left bg-white hairline transition-all w-full ${
                                            userProfile?.is_admin 
                                                ? 'hover:bg-neutral-900 hover:text-white cursor-pointer' 
                                                : 'opacity-50 cursor-not-allowed'
                                        }`}
                                    >
                                        <span className="font-mono text-sm uppercase tracking-wider flex items-center gap-2">
                                            <span className="px-1.5 py-0.5 bg-[#1b263b] text-neutral-0 font-mono text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-120">BETA</span>
                                            Kitchen Stock →
                                        </span>
                                    </button>
                                    {!userProfile?.is_admin && (
                                        <div className="absolute right-full top-0 mr-2 w-48 bg-neutral-900 text-neutral-0 p-3 border border-neutral-200 shadow-lg z-50 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                                            <div className="font-mono text-xs uppercase tracking-wider mb-1">
                                                COMING SOON
                                            </div>
                                            <div className="text-sm">
                                                Kitchen Stock is currently available for admin users only.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Occasion Detail Modal */}
            {selectedOccasionId && (
                <OccasionDetailModal
                    occasionId={selectedOccasionId}
                    onClose={() => {
                        setSelectedOccasionId(null);
                        loadHubData(); // Refresh data when modal closes
                    }}
                    onUpdate={loadHubData}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div 
                        className="bg-white hairline max-w-md w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="font-sans text-2xl font-medium mb-4" style={{ color: colorTokens.oxfordBlue }}>
                            Delete Occasion
                        </h2>
                        <p className="font-sans text-base text-neutral-900 mb-4">
                            Are you sure you want to delete <strong>"{deleteConfirmData.occasion.name}"</strong>?
                        </p>
                        
                        {(deleteConfirmData.groceryListsCount > 0 || deleteConfirmData.menuPlansCount > 0) && (
                            <div className="mb-4 p-4 bg-yellow-50 hairline">
                                <p className="font-mono text-sm text-yellow-900 mb-2">
                                    ⚠️ This occasion has related items:
                                </p>
                                <ul className="list-disc list-inside font-sans text-sm text-yellow-900 space-y-1">
                                    {deleteConfirmData.groceryListsCount > 0 && (
                                        <li>{deleteConfirmData.groceryListsCount} grocery list{deleteConfirmData.groceryListsCount !== 1 ? 's' : ''}</li>
                                    )}
                                    {deleteConfirmData.menuPlansCount > 0 && (
                                        <li>{deleteConfirmData.menuPlansCount} menu plan{deleteConfirmData.menuPlansCount !== 1 ? 's' : ''}</li>
                                    )}
                                </ul>
                                <p className="font-sans text-sm text-yellow-900 mt-2">
                                    These will not be deleted, but will no longer be linked to this occasion.
                                </p>
                            </div>
                        )}

                        <div className="flex gap-2">
                            <button
                                onClick={async () => {
                                    try {
                                        const token = await user.getIdToken();
                                        await deleteOccasion(deleteConfirmData.occasion.id, token);
                                        setSuccessMessage('Occasion deleted successfully');
                                        setDeleteConfirmData(null);
                                        loadHubData();
                                        
                                        // Dispatch event to notify other pages
                                        window.dispatchEvent(new CustomEvent('occasionDeleted', {
                                            detail: { occasionId: deleteConfirmData.occasion.id }
                                        }));
                                        
                                        setTimeout(() => setSuccessMessage(null), 3000);
                                    } catch (err) {
                                        setError(err.message || 'Failed to delete occasion');
                                        setDeleteConfirmData(null);
                                    }
                                }}
                                className="flex-1 px-4 py-2 font-mono text-sm uppercase tracking-wider bg-red-500 text-white hover:bg-red-600 transition-all border-none"
                            >
                                Delete
                            </button>
                            <button
                                onClick={() => setDeleteConfirmData(null)}
                                className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-white transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanningHubPage;

